import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';
import { CardComponent } from 'src/app/shared/components/master-detail-layout/card/card.component';
import { PrinterOperationService } from '../../../services/printer-operation.service';
import { dgfeResultDialog, FiscalPrinters } from 'app/ajs-upgraded-providers';
import { PrintersModuleService } from '../../../services/printers-module.service';
import { AlertDialogService, DailyClosingDialogService } from 'src/app/dialogs';
import { Printers } from 'tilby-models';
import { DocumentPrinterOptions } from 'src/app/shared/model/document-printer.model';
import { TilbyDatePipe } from '@tilby/tilby-ui-lib/pipes/tilby-date';
import { EnvironmentInfoService } from 'src/app/core';
@Component({
  selector: 'app-printer-operations',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    ActionBtnComponent,
    MatFormFieldModule, ReactiveFormsModule,
    MatInputModule, MatButtonModule, MatButtonToggleModule,
    MatDatepickerModule, MatSlideToggleModule,
    TranslateModule
  ],
  templateUrl: './printer-operations.component.html',
  styleUrl: './printer-operations.component.scss'
})
export class PrinterOperationsComponent implements OnDestroy {

  private readonly printerOperationService = inject(PrinterOperationService);
  private readonly translateService = inject(TranslateService);
  private readonly fiscalPrintersService = inject(FiscalPrinters);
  private readonly printerModuleService = inject(PrintersModuleService);
  private readonly dailyClosingDialogService = inject(DailyClosingDialogService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly tilbyDatePipe = inject(TilbyDatePipe);
  private readonly dgfeResultDialog = inject(dgfeResultDialog);
  private readonly environmentInfoService = inject(EnvironmentInfoService);

  private isMobileApp = this.environmentInfoService.isMobileApp();
  protected downloadAllLink: string | null = null;
  protected downloadSelectionLink: string | null = null;

  // the fiscal printers service only needs the printer id to resolve the driver
  private documentPrinterOptions(printerId?: number) {
    return { printer: { id: printerId } } as DocumentPrinterOptions;
  }

  protected cashForm = new FormGroup({
    type: new FormControl<'deposit' | 'withdrawal'>('deposit', { nonNullable: true }),
    amount: new FormControl<number | null>(null, [
      Validators.min(0.01),
      Validators.pattern(/^[0-9]*(\.([0-9]){1,2})?$/)
      // Validators.pattern(/^\d*(\.\d{1,2})?$/)
    ])
  });

  protected fiscalMemoryForm = new FormGroup({
    from_date: new FormControl<string | null>(null),
    to_date: new FormControl<string | null>(null),
    corrispettivi: new FormControl<boolean>(false)
  });

  protected dgfeForm = new FormGroup({
    from_date: new FormControl<string | null>(null),
    to_date: new FormControl<string | null>(null),
  });

  executeCashOperation() {
    if (this.cashForm.valid) {
      const { type, amount } = this.cashForm.getRawValue();
      
      if (type === 'deposit') {
        this.deposit(amount!);
      } else {
        this.withdrawal(amount!);
      }

      this.cashForm.controls.amount.reset();
    }
  }

  get currentCashType() {
    return this.cashForm.controls.type.value;
  }

  async deposit(amount: number) {
    // console.log("Versamento di:", amount);
    const printerId = this.printerModuleService.selection()?.printer?.id;
    await this.printerOperationService.execute({
      confirmMessage: this.translateService.instant('PRINTERS.DETAIL.WANT_TO_DEPOSIT'),
      operationFn: () => this.fiscalPrintersService.deposit(this.documentPrinterOptions(printerId), amount),
      successMessage: this.translateService.instant('PRINTERS.DETAIL.DEPOSIT_COMPLETED'),
      printerId: printerId?.toString() ?? ''
    });
  }

  async withdrawal(amount: number) {
    // console.log("Prelievo di:", amount);
    const printerId = this.printerModuleService.selection()?.printer?.id;
    await this.printerOperationService.execute({
      confirmMessage: this.translateService.instant('PRINTERS.DETAIL.WANT_TO_WITHDRAWAL'),
      operationFn: () => this.fiscalPrintersService.withdrawal(this.documentPrinterOptions(printerId), amount),
      successMessage: this.translateService.instant('PRINTERS.DETAIL.WITHDRAWAL_COMPLETED'),
      printerId: printerId?.toString() ?? ''
    });
  }
  
  protected cashControl = new FormControl<number | null>(null, [
    Validators.min(0.01),
    Validators.pattern(/^\d*(\.\d{1,2})?$/)
  ]);

  // deposit() {
  //   if (this.cashControl.valid) {
  //     const amount = this.cashControl.value;
  //     console.log("Versamento di:", amount);
      
  //     this.cashControl.reset();
  //   }
  // }

  // withdrawal() {
  //   if (this.cashControl.valid) {
  //     const amount = this.cashControl.value;
  //     console.log("Prelievo di:", amount);
      
  //     this.cashControl.reset();
  //   }
  // }

  async dailyRead() {
    const printerId = this.printerModuleService.selection()?.printer?.id;
    await this.printerOperationService.execute({
      confirmMessage: this.translateService.instant('PRINTERS.DETAIL.WANT_TO_READ_DAILY'),
      operationFn: () => this.fiscalPrintersService.dailyRead(this.documentPrinterOptions(printerId)),
      successMessage: this.translateService.instant('PRINTERS.DETAIL.DAILY_READ_COMPLETED'),
      printerId: printerId?.toString() ?? ''
    });
  }

  async dailyClosing() {
    const printer = this.printerModuleService.selection()?.printer as Printers;
    await this.dailyClosingDialogService.openDialog({ data: structuredClone(printer) });
  }

  async printFiscalMemory() {
    const printerId = this.printerModuleService.selection()?.printer?.id;
    await this.printerOperationService.execute({
      confirmMessage: this.translateService.instant('PRINTERS.DETAIL.WANT_TO_PRINT'),
      operationFn: () => this.fiscalPrintersService.printFiscalMemory(this.documentPrinterOptions(printerId)),
      successMessage: this.translateService.instant('PRINTERS.DETAIL.PRINT_COMPLETED'),
      printerId: printerId?.toString() ?? ''
    });
  }

  async printFiscalMemoryBetween() {
    const fromDate = this.fiscalMemoryForm.get('from_date')?.value || '';
    const toDate = this.fiscalMemoryForm.get('to_date')?.value || '';

    if(fromDate > toDate) {
      await this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('PRINTERS.DETAIL.DATE_ERROR')
        }
      });
      return;
    }

    const corrispettiviFlag = this.fiscalMemoryForm.get('corrispettivi')?.value ?? false;
    const printerId = this.printerModuleService.selection()?.printer?.id;

    await this.printerOperationService.execute({
      confirmMessage: this.translateService.instant('PRINTERS.DETAIL.WANT_TO_PRINT'),
      operationFn: () => this.fiscalPrintersService.printFiscalMemoryBetween(
        this.documentPrinterOptions(printerId),
        corrispettiviFlag,
        this.tilbyDatePipe.transform(fromDate, 'DDMMYY'),
        this.tilbyDatePipe.transform(toDate, 'DDMMYY'),
      ),
      successMessage: this.translateService.instant('PRINTERS.DETAIL.PRINT_COMPLETED'),
      printerId: printerId?.toString() ?? ''
    });
  }

  async readDgfe() {
    const printerId = this.printerModuleService.selection()?.printer?.id;
    await this.printerOperationService.execute({
      confirmMessage: this.translateService.instant('PRINTERS.DETAIL.WANT_TO_READ_DGFE'),
      operationFn: async () => {
        const result = await this.fiscalPrintersService.readDgfe( { id: printerId } as Printers );
        this.dgfeResultDialog.show(null, null, result)
      },
      printerId: printerId?.toString() ?? ''
    });
  }

  async readDgfeBetween() {
    const fromDate = this.dgfeForm.get('from_date')?.value || '';
    const toDate = this.dgfeForm.get('to_date')?.value || '';

    if(fromDate > toDate) {
      await this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('PRINTERS.DETAIL.DATE_ERROR')
        }
      });
      return;
    }

    const printerId = this.printerModuleService.selection()?.printer?.id;

    await this.printerOperationService.execute({
      confirmMessage: this.translateService.instant('PRINTERS.DETAIL.WANT_TO_READ_DGFE'),
      operationFn: async () => {
        const result = await this.fiscalPrintersService.readDgfeBetween(
          { id: printerId } as Printers,
          this.tilbyDatePipe.transform(fromDate, 'DDMMYY'),
          this.tilbyDatePipe.transform(toDate, 'DDMMYY'),
        );
        this.dgfeResultDialog.show(
          this.tilbyDatePipe.transform(fromDate, 'DDMMYY'), 
          this.tilbyDatePipe.transform(toDate, 'DDMMYY'), 
          result
        );
      },
      printerId: printerId?.toString() ?? ''
    });
  }

  async printDgfe() {
    const printerId = this.printerModuleService.selection()?.printer?.id;
    await this.printerOperationService.execute({
      confirmMessage: this.translateService.instant('PRINTERS.DETAIL.PRINT_ALL_DGFE'),
      operationFn: async () => this.fiscalPrintersService.printDgfe(this.documentPrinterOptions(printerId)),
      successMessage: this.translateService.instant('PRINTERS.DETAIL.DGFE_PRINTED'),
      printerId: printerId?.toString() ?? ''
    });
  }

  async printDgfeBetween() {
    const fromDate = this.dgfeForm.get('from_date')?.value || '';
    const toDate = this.dgfeForm.get('to_date')?.value || '';

    if(fromDate > toDate) {
      await this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('PRINTERS.DETAIL.DATE_ERROR')
        }
      });
      return;
    }

    const printerId = this.printerModuleService.selection()?.printer?.id;

    await this.printerOperationService.execute({
      confirmMessage: this.translateService.instant('PRINTERS.DETAIL.PRINT_DGFE'),
      operationFn: () => this.fiscalPrintersService.printDgfeBetween(
        this.documentPrinterOptions(printerId),
        this.tilbyDatePipe.transform(fromDate, 'DDMMYY'),
        this.tilbyDatePipe.transform(toDate, 'DDMMYY'),
      ),
      successMessage: this.translateService.instant('PRINTERS.DETAIL.PRINT_COMPLETED'),
      printerId: printerId?.toString() ?? ''
    });
  }

  async saveDgfe() {
    if(this.isMobileApp) {
      await this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('PRINTERS.DETAIL.SAVE_ONLY_ON_BROWSERS')
        }
      });
      return;
    }

    this.revokeLinks();

    const printerId = this.printerModuleService.selection()?.printer?.id;
    await this.printerOperationService.execute({
      confirmMessage: this.translateService.instant('PRINTERS.DETAIL.SAVE_DGFE'),
      operationFn: async () => { 
        const result = await this.fiscalPrintersService.readDgfe({ id: printerId } as Printers);
        const file = new Blob([result], { type: 'text/plain' });
        this.printerOperationService.triggerDownload(file, 'dgfe_read.txt');
        this.downloadAllLink = window.URL.createObjectURL(file);
      },
      successMessage: this.translateService.instant('PRINTERS.DETAIL.DGFE_PRINTED'),
      printerId: printerId?.toString() ?? ''
    });
  }

  async saveDgfeBetween() {
    if(this.isMobileApp) {
      await this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('PRINTERS.DETAIL.SAVE_ONLY_ON_BROWSERS')
        }
      });
      return;
    }

    const fromDate = this.dgfeForm.get('from_date')?.value || '';
    const toDate = this.dgfeForm.get('to_date')?.value || '';

    if(fromDate > toDate) {
      await this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('PRINTERS.DETAIL.DATE_ERROR')
        }
      });
      return;
    }

    this.revokeLinks();

    const printerId = this.printerModuleService.selection()?.printer?.id;

    await this.printerOperationService.execute({
      confirmMessage: this.translateService.instant('PRINTERS.DETAIL.PRINT_DGFE'),
      operationFn: async () => {
        const result = await this.fiscalPrintersService.readDgfeBetween(
          { id: printerId } as Printers,
          this.tilbyDatePipe.transform(fromDate, 'DDMMYY'),
          this.tilbyDatePipe.transform(toDate, 'DDMMYY'),
        );
        const file = new Blob([result], { type: 'text/plain' });
        this.printerOperationService.triggerDownload(file, this.getDownloadSelectionLinkFilename(fromDate, toDate));
        this.downloadSelectionLink = window.URL.createObjectURL(file);
      },
      printerId: printerId?.toString() ?? ''
    });
  }

  protected getDownloadSelectionLinkFilename(fromDate: string, toDate: string) {
    return 'dgfe_read_'+ this.tilbyDatePipe.transform(fromDate, 'DDMMYY') + '_' + this.tilbyDatePipe.transform(toDate, 'DDMMYY') + '.txt';
  }

  ngOnDestroy() {
    this.revokeLinks();
  }

  private revokeLinks() {
    if (this.downloadAllLink) URL.revokeObjectURL(this.downloadAllLink);
    if (this.downloadSelectionLink) URL.revokeObjectURL(this.downloadSelectionLink);
  }

  downloadFile(url: string, name?: string) {
    const link = document.createElement('a');
    link.href = url;
    if(name) link.download = name;
    link.click();
  }
}
